import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Fish,
  MapPin,
  Sparkles,
  Calendar,
  Waves,
  Smile,
  Compass,
  Camera,
  Heart,
  Clock,
  Award,
  ChevronRight,
  ShieldCheck,
  CheckCircle2
} from 'lucide-react';
import { fetchCollection } from '../lib/cms';
import { getOptimizedImageUrl } from '../lib/utils';

const Home = () => {
  const [adventures, setAdventures] = useState([]);
  const [spots, setSpots] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [activeSpotIndex, setActiveSpotIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAllContent = async () => {
      try {
        const [advData, spotsData, galleryData] = await Promise.all([
          fetchCollection('adventures'),
          fetchCollection('spots'),
          fetchCollection('gallery')
        ]);

        // Helper to parse Danish date strings
        const parseDanishDate = (dateStr) => {
          if (!dateStr) return new Date(0);
          const months = {
            'januar': 0, 'jan': 0, 'februar': 1, 'feb': 1,
            'marts': 2, 'mar': 2, 'april': 3, 'apr': 3,
            'maj': 4, 'juni': 5, 'jun': 5, 'juli': 6, 'jul': 6,
            'august': 7, 'aug': 7, 'september': 8, 'sep': 8,
            'oktober': 9, 'okt': 9, 'november': 10, 'nov': 10,
            'december': 11, 'dec': 11
          };
          const parts = dateStr.toLowerCase().replace('.', '').split(' ');
          if (parts.length !== 3) return new Date(0);
          const day = parseInt(parts[0], 10);
          const month = months[parts[1]];
          const year = parseInt(parts[2], 10);
          if (isNaN(day) || month === undefined || isNaN(year)) return new Date(0);
          return new Date(year, month, day);
        };

        const sortedAdv = [...advData].sort((a, b) => {
          const dateA = parseDanishDate(a.date);
          const dateB = parseDanishDate(b.date);
          if (dateB.getTime() === dateA.getTime()) {
            return new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime();
          }
          return dateB.getTime() - dateA.getTime();
        });

        setAdventures(sortedAdv.slice(0, 3));
        setSpots(spotsData);
        setGalleryItems(galleryData.slice(0, 6));
      } catch (error) {
        console.error('Error fetching home content:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAllContent();
  }, []);

  const stats = [
    { label: 'Vandtæt pels', value: '100%', icon: ShieldCheck, sub: 'Klar til ethvert dyk' },
    { label: 'Dagens sild', value: '14+', icon: Fish, sub: 'Fanget ved Aggersund' },
    { label: 'Blå bukser', value: '1 par', icon: Sparkles, sub: 'Skræddersyet til vandet' },
    { label: 'Gode venner', value: '25+', icon: Heart, sub: 'Fra krabber til svaner' },
  ];

  const dailyRoutine = [
    {
      time: '06:00',
      title: 'Morgendyp & Silde-jagt',
      description: 'Dagen starter altid med et friskt spring i fjorden og en hurtig morgen-snack på det dybe vand.',
      tag: 'Morgen'
    },
    {
      time: '10:30',
      title: 'Posering ved Havnekanten',
      description: 'Jeg kigger forbi fiskerbådene i Glyngøre for at hilse på de lokale og se om der falder godbidder af.',
      tag: 'Formiddag'
    },
    {
      time: '13:00',
      title: 'Den Hellige Middagslur',
      description: 'Kun de bedste, solvarme sten ved kysten er gode nok til en 2-timers odder-lur.',
      tag: 'Middag'
    },
    {
      time: '16:30',
      title: 'Besøg hos Krabben Kalle',
      description: 'Tid til leg, high-fives og øvelser i at svømme sidelæns i det lave vand.',
      tag: 'Eftermiddag'
    },
    {
      time: '20:00',
      title: 'Solnedgang & Røverhistorier',
      description: 'Jeg sætter mig med ræven Felix på træstammen og nyder aftenhimlens gyldne skær over vandet.',
      tag: 'Aften'
    }
  ];

  const otterFacts = [
    {
      title: 'Pels med over 100.000 hår/cm²',
      desc: 'Det er grunden til, at jeg aldrig fryser, selv når fjorden er iskold om vinteren!',
      badge: 'Super-pels'
    },
    {
      title: 'Uovertruffen svømmestil',
      desc: 'Med min strømlinede krop og svømmehud kan jeg overhale de fleste sild uden problemer.',
      badge: 'Hurtigsvømmer'
    },
    {
      title: 'Verdens første odder i bukser',
      desc: 'Ingen ved helt hvor de kom fra, men de passer perfekt til min hale og mit gode humør.',
      badge: 'Ikonisk Stil'
    },
    {
      title: 'Venskaber på tværs af arter',
      desc: 'I Limfjorden er der plads til alle – svaner, krabber, sæler og endda talende papegøjer!',
      badge: 'Fjord-hygge'
    }
  ];

  return (
    <>
      <Helmet>
        <title>LimfjordsOdderen | Velkommen til mit våde univers</title>
        <meta
          name="description"
          content="Mød Otto – Limfjordens gladeste odder i blå bukser. Læs om vilde eventyr, hemmelige spots, sjove dyrevenner og sild-fester."
        />
      </Helmet>

      {/* 1. HERO SECTION (Light & Fresh Fjord Aesthetic) */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-gradient-to-b from-sky-100/70 via-blue-50/40 to-white text-slate-900">
        {/* Fjord Water Background Glow / Image */}
        <div className="absolute inset-0 z-0">
          <img
            src={getOptimizedImageUrl(
              'https://assets.auxodev.dk/assets/b3f2b1ea-cc94-4b30-89c2-3e141ee932d1/bc49f88c-88e4-4e70-a05a-7ce25ab1fcb9.png',
              { width: 1920, quality: 85 }
            )}
            alt="LimfjordsOdderen i Limfjorden"
            className="w-full h-full object-cover object-center opacity-70 scale-105 transition-transform duration-10000 ease-out"
          />
          {/* Light gradients tailored for high image visibility and crisp text contrast */}
          <div className="absolute inset-0 bg-gradient-to-t from-white via-white/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-white/85 via-white/50 to-sky-100/10" />
        </div>

        {/* Decorative soft circles */}
        <div className="absolute top-20 right-10 w-96 h-96 bg-blue-200/40 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-10 left-10 w-80 h-80 bg-cyan-200/30 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 w-full">
          <div className="max-w-3xl">
            {/* Live Location / Status Badge */}
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/90 border border-blue-200 text-blue-800 text-sm font-semibold mb-6 shadow-sm backdrop-blur-md">
              <span className="flex h-2.5 w-2.5 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-500 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-600"></span>
              </span>
              <span>📍 Svømmer pt. omkring Fur &bull; 🐟 Dagens fangst: 14 sild</span>
            </div>

            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.1] mb-6 text-slate-900">
              Velkommen til mit <br />
              <span className="bg-gradient-to-r from-blue-600 via-sky-600 to-cyan-600 bg-clip-text text-transparent">
                våde & vilde univers
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-slate-600 mb-8 leading-relaxed font-normal">
              Jeg hedder <strong>Otto</strong> – Limfjordens ubestridte charmør, selvudnævnt verdensmester i maveplaskere og Danmarks eneste odder, der rocker et par knaldblå bukser. Dyk ned i mine historier, opdag de bedste fiske-steder og mød mine sjove venner.
            </p>

            <div className="flex flex-wrap gap-4 items-center">
              <Link
                to="/eventyr"
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-4 rounded-full text-base sm:text-lg flex items-center gap-2 shadow-lg shadow-blue-600/25 transition-all transform hover:-translate-y-0.5"
              >
                Læs mine eventyr
                <ArrowRight className="h-5 w-5" />
              </Link>
              <Link
                to="/eventyr/mysteriet-om-de-blaa-bukser"
                className="bg-white hover:bg-slate-50 text-slate-800 font-bold px-6 py-4 rounded-full text-base sm:text-lg border border-slate-200 shadow-sm transition-all flex items-center gap-2 hover:border-blue-300"
              >
                <Sparkles className="h-5 w-5 text-amber-500" />
                Historien om de Blå Bukser
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom subtle border wave */}
        <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-blue-50/50 to-transparent pointer-events-none" />
      </section>

      {/* 2. STATS & HIGHLIGHTS BANNER (Light Theme) */}
      <section className="bg-white border-y border-blue-100 text-slate-800 py-8 relative z-20 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  className="flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-br from-blue-50/70 via-sky-50/40 to-slate-50 border border-blue-100/80 hover:border-blue-300 transition-all shadow-xs"
                >
                  <div className="p-3 rounded-xl bg-blue-600 text-white shrink-0 shadow-sm shadow-blue-600/20">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="text-2xl sm:text-3xl font-extrabold text-slate-900">{stat.value}</div>
                    <div className="text-sm font-bold text-slate-700">{stat.label}</div>
                    <div className="text-xs text-slate-500 hidden sm:block">{stat.sub}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 3. INTRODUCTION / ABOUT THE OTTER (Light Theme) */}
      <section className="py-20 lg:py-28 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Column: Visual collage */}
            <div className="lg:col-span-6 relative">
              <div className="relative mx-auto max-w-md lg:max-w-none">
                {/* Main Image */}
                <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl border-4 border-white aspect-[4/3] sm:aspect-square bg-slate-100 ring-1 ring-slate-200">
                  <img
                    src={getOptimizedImageUrl(
                      'https://assets.auxodev.dk/assets/b3f2b1ea-cc94-4b30-89c2-3e141ee932d1/2bcc6e70-78cc-47cf-8bd5-a355996f6de3.png',
                      { width: 800, quality: 85 }
                    )}
                    alt="Otto nærbillede"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <p className="font-bold text-lg">Otto LimfjordsOdder</p>
                    <p className="text-sm text-slate-200">Professionel sildespiser & hverdagsfilosof</p>
                  </div>
                </div>

                {/* Secondary Floater Image */}
                <div className="absolute -bottom-8 -right-4 sm:-right-8 z-20 w-44 sm:w-56 rounded-2xl overflow-hidden shadow-xl border-4 border-white bg-white ring-1 ring-slate-200 hidden sm:block">
                  <img
                    src={getOptimizedImageUrl(
                      'https://assets.auxodev.dk/assets/b3f2b1ea-cc94-4b30-89c2-3e141ee932d1/b2672745-c405-419d-a3cc-ec7f5c0a2655.png',
                      { width: 400, quality: 80 }
                    )}
                    alt="Odder solbader på en sten"
                    className="w-full aspect-[4/3] object-cover"
                  />
                  <div className="p-3 bg-white text-xs font-bold text-slate-800 text-center border-t border-slate-100">
                    ☀️ Test af sovesten ved Fur
                  </div>
                </div>

                {/* Decorative background blob */}
                <div className="absolute -top-10 -left-10 w-72 h-72 bg-blue-100 rounded-full blur-3xl opacity-70 -z-10" />
                <div className="absolute -bottom-10 -right-10 w-72 h-72 bg-cyan-100 rounded-full blur-3xl opacity-60 -z-10" />
              </div>
            </div>

            {/* Right Column: Bio & Core Values */}
            <div className="lg:col-span-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-100 text-blue-800 text-sm font-bold mb-6">
                <Smile className="h-4 w-4 text-blue-600" /> Mød Fjordenes Konge
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight mb-6">
                Et liv fyldt med <br />
                <span className="text-blue-600">plask, personlighed & sild</span>
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Når man bor i Danmarks smukkeste fjord, er hver dag et nyt eventyr. Jeg bruger mine dage på at udforske hemmelige vige, svømme om kap med sild og pleje mit legendariske overskæg.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3.5 p-4 rounded-2xl bg-slate-50 border border-slate-200/70">
                  <div className="p-2.5 rounded-xl bg-blue-600 text-white shrink-0 mt-0.5 shadow-sm shadow-blue-600/20">
                    <Fish className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-base">Fiske-feinschmecker</h4>
                    <p className="text-slate-600 text-sm">Jeg ved præcis, hvilke strømfald der gemmer de saftigste sild og sprødeste krabber.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3.5 p-4 rounded-2xl bg-slate-50 border border-slate-200/70">
                  <div className="p-2.5 rounded-xl bg-cyan-600 text-white shrink-0 mt-0.5 shadow-sm shadow-cyan-600/20">
                    <Sparkles className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-base">Danmarks mest velklædte odder</h4>
                    <p className="text-slate-600 text-sm">De knaldblå bukser er blevet mit varemærke – selv svanerne sender misundelige blikke.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3.5 p-4 rounded-2xl bg-slate-50 border border-slate-200/70">
                  <div className="p-2.5 rounded-xl bg-amber-500 text-white shrink-0 mt-0.5 shadow-sm shadow-amber-500/20">
                    <Heart className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-base">Altid klar på nye venskaber</h4>
                    <p className="text-slate-600 text-sm">Uanset om du har vinger, finner, pels eller bare to ben, er du altid velkommen ved fjorden.</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <Link
                  to="/om-mig"
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-7 py-3.5 rounded-full transition-all text-base shadow-md shadow-blue-600/25"
                >
                  Læs hele min historie
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-bold px-4 py-3 text-base transition-colors"
                >
                  Send mig en hilsen <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 4. SPOTLIGHT: MYSTERIET OM DE BLÅ BUKSER (Light Theme Showcase) */}
      <section className="py-20 lg:py-24 bg-gradient-to-br from-blue-50 via-sky-50 to-indigo-50/50 text-slate-900 relative overflow-hidden border-y border-blue-100">
        {/* Subtle decorative circles */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-200/40 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-200/40 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-white rounded-3xl p-8 sm:p-12 lg:p-16 shadow-xl border border-blue-100">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              
              <div className="lg:col-span-7 space-y-6">
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-100 text-blue-800 text-sm font-bold">
                  <Sparkles className="h-4 w-4 text-amber-500" /> Varemærket over dem alle
                </div>

                <h2 className="text-3xl sm:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight">
                  Mysteriet om de <br />
                  <span className="text-blue-600 underline decoration-wavy decoration-blue-300">
                    Knaldblå Bukser
                  </span>
                </h2>

                <p className="text-lg text-slate-600 leading-relaxed">
                  Har du nogensinde set en odder i bukser? Det hele startede en tidlig tirsdag morgen ved fjordens bred. Et par skræddersyede, knaldblå bukser lå og flød – som skabt til en odder med stil og selvtillid.
                </p>

                <p className="text-base text-slate-700 leading-relaxed italic border-l-4 border-blue-500 pl-4 py-1 bg-blue-50/50 rounded-r-lg">
                  "Fiskene kigger en ekstra gang, og mågerne kan slet ikke skjule deres misundelse. Mit liv og min stil er aldrig blevet det samme siden!"
                </p>

                <div className="pt-2 flex flex-wrap gap-4">
                  <Link
                    to="/eventyr/mysteriet-om-de-blaa-bukser"
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-extrabold px-8 py-4 rounded-full text-base shadow-lg shadow-blue-600/25 transition-all transform hover:scale-105"
                  >
                    Læs hele bukse-historien
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-5 relative">
                <div className="relative mx-auto max-w-sm lg:max-w-none">
                  <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl border-4 border-white ring-1 ring-blue-100 rotate-2 hover:rotate-0 transition-transform duration-500 bg-slate-100">
                    <img
                      src={getOptimizedImageUrl(
                        'https://assets.auxodev.dk/assets/b3f2b1ea-cc94-4b30-89c2-3e141ee932d1/c6d525e5-ca7f-4eaf-9678-5a5fb3703d06.png',
                        { width: 600, quality: 85 }
                      )}
                      alt="Otto i de blå bukser"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-5 -left-5 bg-blue-600 text-white font-bold text-xs sm:text-sm px-4 py-2.5 rounded-xl shadow-lg border-2 border-white">
                    👖 Limfjordens bedst klædte!
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* 5. SENESTE EVENTYR FRA FJORDEN (Dynamisk fra CMS - Light Theme) */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-100 text-blue-800 text-sm font-bold mb-4">
                <Compass className="h-4 w-4 text-blue-600" /> Dagbog & Oplevelser
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
                Seneste eventyr fra fjorden
              </h2>
              <p className="text-lg text-slate-600 mt-2 max-w-2xl">
                Læs mine nyeste beretninger om uventede venskaber, sildefester og snedige tricks.
              </p>
            </div>
            <Link
              to="/eventyr"
              className="inline-flex items-center gap-2 font-bold text-blue-600 hover:text-blue-700 text-base group self-start md:self-end"
            >
              <span>Se alle {adventures.length > 0 ? 'eventyr' : ''}</span>
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {loading ? (
              [1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-3xl overflow-hidden shadow-sm border border-slate-100 animate-pulse">
                  <div className="h-60 bg-slate-200" />
                  <div className="p-6 space-y-4">
                    <div className="h-4 bg-slate-200 w-1/3 rounded" />
                    <div className="h-6 bg-slate-200 w-3/4 rounded" />
                    <div className="h-4 bg-slate-200 w-full rounded" />
                    <div className="h-4 bg-slate-200 w-5/6 rounded" />
                  </div>
                </div>
              ))
            ) : adventures.length === 0 ? (
              <div className="col-span-3 text-center py-12 text-slate-500">
                Ingen eventyr fundet lige nu.
              </div>
            ) : (
              adventures.map((adv) => (
                <article
                  key={adv.id}
                  className="group bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl border border-slate-200 transition-all duration-300 flex flex-col hover:-translate-y-1"
                >
                  <div className="relative h-60 overflow-hidden bg-slate-100">
                    <img
                      src={getOptimizedImageUrl(adv.image?.[0], { width: 600, quality: 80, height: 400 })}
                      alt={adv.title?.da || 'Eventyr'}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-md px-3.5 py-1.5 rounded-full text-xs font-bold text-slate-800 flex items-center gap-1.5 shadow-sm border border-slate-100">
                      <Calendar className="h-3.5 w-3.5 text-blue-600" />
                      {adv.date}
                    </div>
                  </div>

                  <div className="p-6 sm:p-7 flex-grow flex flex-col">
                    <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors leading-snug">
                      {adv.title?.da}
                    </h3>
                    
                    <div
                      className="text-slate-600 text-sm leading-relaxed mb-6 line-clamp-3"
                      dangerouslySetInnerHTML={{ __html: adv.content?.da || '' }}
                    />

                    <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between">
                      <Link
                        to={`/eventyr/${adv.slug?.da}`}
                        className="text-blue-600 font-bold text-sm inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all"
                      >
                        Læs historien <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </article>
              ))
            )}
          </div>
        </div>
      </section>

      {/* 6. INTERAKTIVT SPOTLIGHT: MINE FAVORIT-SPOTS I LIMFJORDEN (Dynamisk fra CMS - Light Theme) */}
      <section className="py-20 lg:py-28 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cyan-100 text-cyan-800 text-sm font-bold mb-4">
              <MapPin className="h-4 w-4 text-cyan-600" /> Fjordens Hemmelige Steder
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
              Ottos Favorit-Spots i Limfjorden
            </h2>
            <p className="text-lg text-slate-600 mt-3">
              Her er de steder, hvor chancerne er størst for at spotte mit overskæg i vandoverfladen eller se mig tage en lur i solen.
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse bg-slate-100 rounded-3xl h-80" />
              ))}
            </div>
          ) : spots.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {spots.map((spot, index) => {
                const isSelected = index === activeSpotIndex;
                return (
                  <div
                    key={spot.id || index}
                    onClick={() => setActiveSpotIndex(index)}
                    className={`cursor-pointer rounded-3xl overflow-hidden transition-all duration-300 border-2 flex flex-col ${
                      isSelected
                        ? 'border-blue-600 bg-blue-50/50 shadow-xl ring-4 ring-blue-500/10'
                        : 'border-slate-200 bg-white hover:border-blue-300 hover:shadow-md'
                    }`}
                  >
                    <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                      <img
                        src={getOptimizedImageUrl(spot.image?.[0], { width: 600, quality: 80 })}
                        alt={spot.name?.da}
                        className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                      />
                      <div className="absolute top-4 right-4 bg-slate-900/80 backdrop-blur-md text-white text-xs font-bold px-3 py-1.5 rounded-full">
                        {spot.location_type || 'Fjord-spot'}
                      </div>
                    </div>

                    <div className="p-6 sm:p-7 flex-grow flex flex-col">
                      <div className="flex items-center gap-2 text-blue-600 text-xs font-bold uppercase tracking-wider mb-2">
                        <MapPin className="h-3.5 w-3.5" /> Limfjorden Spot #{index + 1}
                      </div>
                      <h3 className="text-2xl font-bold text-slate-900 mb-3">
                        {spot.name?.da}
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed mb-4 flex-grow">
                        {spot.description?.da}
                      </p>

                      <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500 font-medium">
                        <span>🐟 Fiske-potentiale: Høj</span>
                        <span>☀️ Sove-faktor: 10/10</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      </section>

      {/* 7. EN TYPISK DAG I MIT ODDER-LIV (Light Theme Timeline) */}
      <section className="py-20 lg:py-28 bg-gradient-to-b from-blue-50/60 via-sky-50/30 to-slate-50 text-slate-900 relative overflow-hidden border-t border-blue-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-100 text-blue-800 text-sm font-bold mb-4">
              <Clock className="h-4 w-4 text-blue-600" /> Døgnrytmen i Vandkanten
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
              En typisk dag i mit odder-liv
            </h2>
            <p className="text-lg text-slate-600 mt-3">
              Folk spørger tit: "Hvad laver en odder egentlig hele dagen?" Her er mit stramme aktivitetsskema!
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-5">
            {dailyRoutine.map((item, idx) => (
              <div
                key={idx}
                className="bg-white border border-slate-200/90 rounded-2xl p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center gap-6 hover:border-blue-400 hover:shadow-md transition-all shadow-xs"
              >
                <div className="flex items-center sm:flex-col justify-between sm:justify-center sm:w-28 shrink-0 pb-3 sm:pb-0 border-b sm:border-b-0 sm:border-r border-slate-100 pr-0 sm:pr-6">
                  <span className="text-2xl sm:text-3xl font-extrabold text-blue-600 font-mono">
                    {item.time}
                  </span>
                  <span className="text-xs uppercase tracking-wider font-bold text-blue-700 px-2 py-0.5 bg-blue-50 rounded mt-1">
                    {item.tag}
                  </span>
                </div>

                <div className="flex-grow">
                  <h4 className="text-xl font-bold text-slate-900 mb-1.5">{item.title}</h4>
                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. FOTOGALLERI HIGHLIGHTS: MØD MINE VENNER (Fra CMS - Light Theme) */}
      <section className="py-20 lg:py-28 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-100 text-blue-800 text-sm font-bold mb-4">
                <Camera className="h-4 w-4 text-blue-600" /> Fotoalbummet
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
                Mød mine bedste venner i fjorden
              </h2>
              <p className="text-lg text-slate-600 mt-2 max-w-2xl">
                Fra den kloge svane Sif til krabben Kalle og den talende papegøje Pedro.
              </p>
            </div>
            <Link
              to="/galleri"
              className="inline-flex items-center gap-2 font-bold text-blue-600 hover:text-blue-700 text-base group self-start md:self-end"
            >
              <span>Se det fulde interaktive galleri</span>
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {loading ? (
              [1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="aspect-square bg-slate-200 rounded-2xl animate-pulse" />
              ))
            ) : galleryItems.length > 0 ? (
              galleryItems.map((item, idx) => (
                <Link
                  key={item.id || idx}
                  to="/galleri"
                  className="group relative aspect-square rounded-2xl overflow-hidden bg-slate-100 shadow-xs border border-slate-200 hover:border-blue-400 hover:shadow-lg transition-all"
                >
                  <img
                    src={getOptimizedImageUrl(item.image?.[0], { width: 400, quality: 80, height: 400 })}
                    alt={item.title?.da || 'Galleri billede'}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                    <p className="text-white text-xs font-bold line-clamp-2 leading-tight">
                      {item.title?.da}
                    </p>
                  </div>
                </Link>
              ))
            ) : null}
          </div>
        </div>
      </section>

      {/* 9. FUN FACTS OM ODDERE (Light Theme) */}
      <section className="py-20 bg-slate-50 border-t border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-100 text-amber-800 text-sm font-bold mb-4">
              <Award className="h-4 w-4 text-amber-600" /> Vidste du det?
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              Sjove fakta om Limfjordens oddere
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {otterFacts.map((fact, index) => (
              <div
                key={index}
                className="p-6 rounded-2xl bg-white border border-slate-200/80 hover:border-blue-300 hover:shadow-md transition-all flex flex-col shadow-xs"
              >
                <div className="inline-block self-start px-2.5 py-1 rounded-md bg-blue-100 text-blue-800 text-xs font-extrabold mb-3">
                  {fact.badge}
                </div>
                <h4 className="text-lg font-bold text-slate-900 mb-2">{fact.title}</h4>
                <p className="text-slate-600 text-sm leading-relaxed mt-auto">
                  {fact.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 10. CALL TO ACTION / FJORD-POSTKASSEN (Light & Vibrant Theme) */}
      <section className="py-20 lg:py-24 bg-gradient-to-b from-white to-blue-50/80 text-slate-900 relative overflow-hidden border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-gradient-to-r from-blue-600 via-sky-600 to-cyan-600 rounded-3xl p-8 sm:p-14 lg:p-16 shadow-2xl relative overflow-hidden text-white">
            {/* Background glow & accents */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-white/15 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center relative z-10">
              <div className="lg:col-span-8 space-y-6">
                <span className="inline-block px-4 py-1.5 bg-white/20 backdrop-blur-md rounded-full text-xs font-extrabold tracking-wider uppercase">
                  📬 Fjord-Postkassen er åben
                </span>
                <h2 className="text-3xl sm:text-5xl font-extrabold leading-tight text-white">
                  Har du spottet mig i fjorden <br />
                  <span className="text-blue-100">eller set en god stime sild?</span>
                </h2>
                <p className="text-lg text-blue-50 max-w-2xl leading-relaxed">
                  Send mig en hilsen, tip mig om gode fiske-steder eller upload et billede, hvis du har fanget mig på kamera. Jeg svarer altid, så snart jeg har tørret poterne!
                </p>
                <div className="pt-2 flex flex-wrap gap-4">
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center gap-2 bg-white text-blue-700 hover:bg-blue-50 font-extrabold px-8 py-4 rounded-full text-base sm:text-lg shadow-xl transition-all transform hover:-translate-y-0.5"
                  >
                    Skriv til mig i brevkassen
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                  <Link
                    to="/om-mig"
                    className="inline-flex items-center gap-2 bg-blue-700/60 hover:bg-blue-700 text-white font-bold px-6 py-4 rounded-full text-base sm:text-lg border border-white/20 backdrop-blur-sm transition-all"
                  >
                    Lær mig bedre at kende
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-4 relative flex justify-center">
                <div className="relative w-64 h-64 sm:w-72 sm:h-72 rounded-full overflow-hidden border-4 border-white/60 shadow-2xl bg-blue-800">
                  <img
                    src={getOptimizedImageUrl(
                      'https://assets.auxodev.dk/assets/b3f2b1ea-cc94-4b30-89c2-3e141ee932d1/2bf30a03-67fb-471c-b659-b29ddbaf38c1.png',
                      { width: 600, quality: 85 }
                    )}
                    alt="Nysgerrig odder i vandet"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
